import { CalendarClock, Ellipsis, RefreshCw } from "lucide-react"
import { useState, type ReactNode } from "react"
import {
  PAYOUT_LIST_LIMIT,
  PAYOUT_PREVIEW_CYCLES,
  type MarketplaceId,
  type Payout,
  type PayoutAvailability,
  type PayoutBalance,
  type PayoutBalanceResponse,
  type PayoutFleetEntry,
  type PayoutState,
} from "@nova-ai/shared"
import type { ExchangeRates } from "../../lib/money"
import { formatDateParts, formatDateTime, formatRelativeTime } from "../../lib/format-date"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { cn } from "../../lib/utils"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { Callout } from "../ui/callout"
import { Money } from "../ui/money"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { Table, type TableColumn } from "../ui/table"
import { Tooltip } from "../ui/tooltip"

type PayoutsSectionProps = {
  marketplace: MarketplaceId
  /** This marketplace's mirrored cycles, newest first. Required: the page owns
   *  loading and error for the whole fleet in one read, so a rendered block
   *  always has an answer. */
  data: PayoutFleetEntry
  /** The account's money position, read separately from the cycles because it
   *  answers a different question — and carries the lifetime figure this block's
   *  rows must never be summed into. */
  balance: PayoutBalanceResponse | undefined
  rates: ExchangeRates | undefined
  onSync?: () => void
  isSyncing?: boolean
  syncFailed?: boolean
}

/** A stable anchor for links that target one marketplace within the fleet. */
function payoutsAnchorId(marketplace: MarketplaceId): string {
  return `payouts-${marketplace}`
}

/** `pending` reads "Awaiting transfer", not "Incoming": on a cycle the state means
 *  the document exists and the money has not moved yet — it says nothing about
 *  what is accruing between cycles, which is what the balance band above answers. */
const STATE_LABEL: Record<PayoutState, string> = {
  paid: "Paid",
  pending: "Awaiting transfer",
  failed: "Failed",
}

/** The three figures the marketplace account publishes, in the marketplace's own
 *  vocabulary so the band can be laid beside the seller portal and reconciled
 *  one-to-one. The explanations sit behind a `?` rather than under the numbers —
 *  except "lifetime", which stays in the label: that figure is routinely many
 *  times any single cycle in the table below, and unlabelled it reads as a bug. */
const BALANCE_FIELDS = [
  {
    key: "pending",
    label: "Pending",
    hint: "Earned but not yet due. The marketplace moves it to payable when it closes the amount for an upcoming payout.",
  },
  {
    key: "payable",
    label: "Payable",
    // A negative figure is a real state, observed live on a suspended shop, and
    // "due to us" alone would read as a display bug rather than as a debt.
    hint: "Due to us, waiting for the next payout to carry it. A negative figure runs the other way — we owe the marketplace that much.",
  },
  {
    key: "paid",
    label: "Paid (lifetime)",
    hint: "Everything the marketplace has paid this shop since it opened, as the marketplace itself reports it. It is not the sum of the cycles below — Nova mirrors only as far back as it has synced.",
  },
] as const satisfies readonly { key: keyof PayoutBalance; label: string; hint: string }[]

/** Literal column counts so Tailwind sees every class it must emit. The band is
 *  three balance figures (or one "not captured yet" note) plus the Failed tile
 *  only when something actually failed. */
const BAND_COLUMNS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
}

const STATE_TONE: Record<PayoutState, BadgeTone> = {
  paid: "success",
  pending: "warning",
  failed: "destructive",
}

/**
 * One marketplace's payouts: the account position as of the newest snapshot, then
 * its most recent settlement cycles — expandable to everything Nova has mirrored.
 *
 * The two figures answer to different clocks and both say so: the band is
 * captioned with the day it was captured, the cycle list with how far back it
 * reaches. An expanded list names its own earliest covered day rather than
 * claiming to be every payout ever made — that number is the band's
 * `Paid (lifetime)`, which the marketplace states and nothing here recomputes.
 *
 * Amounts are exact decimal strings from the marketplace — they go into `<Money>`
 * as strings and are formatted digit-for-digit, never via a float.
 */
export function PayoutsSection({
  marketplace,
  data,
  balance,
  rates,
  onSync,
  isSyncing = false,
  syncFailed = false,
}: PayoutsSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const columns = buildColumns(rates)
  const failedTotals = data.totals.filter((total) => !isZeroDecimal(total.failed))
  const hidden = data.payouts.length - PAYOUT_PREVIEW_CYCLES
  const rows = expanded ? data.payouts : data.payouts.slice(0, PAYOUT_PREVIEW_CYCLES)

  return (
    <section
      id={payoutsAnchorId(marketplace)}
      className="scroll-mt-24 overflow-hidden rounded-xl border border-border bg-card shadow-card"
    >
      <PayoutsHeader
        marketplace={marketplace}
        data={data}
        {...(onSync && data.availability !== "not_synced" ? { onSync, isSyncing } : {})}
      />

      {syncFailed ? (
        <div role="status" className="px-5 pt-4">
          <Callout tone="destructive" dense>
            The payout sync could not be started. Try again in a moment.
          </Callout>
        </div>
      ) : null}

      {data.availability === "not_synced" ? (
        <Message
          title="No payouts have been synced yet"
          detail="Refresh this marketplace to tell zero payouts apart from missing history."
          action={onSync ? <SyncButton onSync={onSync} isSyncing={isSyncing} /> : null}
        />
      ) : (
        <>
          {data.truncated ? (
            <div role="status" className="px-5 pt-4">
              <Callout tone="info" dense>
                {`Showing the ${PAYOUT_LIST_LIMIT.toLocaleString()} newest cycles; the totals above cover every mirrored cycle.`}
              </Callout>
            </div>
          ) : null}

          <BalanceBand balance={balance} failedTotals={failedTotals} rates={rates} />

          <SectionCaption>
            {expanded ? "All mirrored payouts" : "Latest payouts"}

            {expanded && data.coveredFrom
              ? ` · since ${formatDateParts(data.coveredFrom).date} — nothing older is mirrored`
              : null}
          </SectionCaption>

          <Table
            columns={columns}
            rows={rows}
            rowKey={(row) => row.payoutId}
            empty="No payout cycles have been mirrored for this marketplace."
            embedded
            dense
            cellAlign="middle"
          />

          {hidden > 0 ? (
            <ExpandToggle
              expanded={expanded}
              total={data.cycleCount}
              onToggle={() => setExpanded(!expanded)}
            />
          ) : null}
        </>
      )}
    </section>
  )
}

function PayoutsHeader({
  marketplace,
  data,
  onSync,
  isSyncing = false,
}: {
  marketplace: MarketplaceId
  data: PayoutFleetEntry
  onSync?: (() => void) | undefined
  isSyncing?: boolean
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-border/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2.5">
        <MarketplaceMark marketplace={marketplace} />

        <h2 className="text-sm font-medium text-foreground">
          {humanizeMarketplaceId(marketplace)}
        </h2>

        <AvailabilityBadge availability={data.availability} />
      </div>

      <div className="flex items-center gap-3">
        {data.lastSyncedAt ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
            title={formatDateTime(data.lastSyncedAt)}
          >
            <CalendarClock className="size-3.5" aria-hidden="true" />

            <span>Synced {formatRelativeTime(data.lastSyncedAt)}</span>
          </span>
        ) : null}

        {onSync ? (
          <SyncButton onSync={onSync} isSyncing={isSyncing} label="Refresh" compact />
        ) : null}
      </div>
    </div>
  )
}

/** The row under the table that opens the rest of the history. Three dots, not a
 *  word: the collapsed block answers "did the last payouts arrive", and anything
 *  louder than a hint would compete with the numbers for attention. It reveals rows
 *  already in hand — the fleet read ships the whole history — so expanding never
 *  waits on the network. */
function ExpandToggle({
  expanded,
  total,
  onToggle,
}: {
  expanded: boolean
  total: number
  onToggle: () => void
}) {
  return (
    <div className="border-t border-border/70 px-5 py-2.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Ellipsis className="size-4" aria-hidden="true" />

        {expanded ? "Show the latest only" : `Show all ${total.toLocaleString()} payouts`}
      </button>
    </div>
  )
}

function buildColumns(rates: ExchangeRates | undefined): readonly TableColumn<Payout>[] {
  return [
    {
      key: "settlement",
      header: "Created",
      render: (row) => (
        <div>
          <div className="font-medium text-foreground">{formatDateTime(row.createdAt)}</div>

          <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{row.payoutId}</div>
        </div>
      ),
    },
    {
      key: "transferredAt",
      header: "Transferred",
      headerHint:
        "When the money actually landed. Cycles are ordered by this date, falling back to the creation date while a transfer is pending.",
      render: (row) =>
        row.transferredAt === null ? (
          <span className="text-muted-foreground/40">—</span>
        ) : (
          <span className="text-foreground">{formatDateTime(row.transferredAt)}</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <StatusBadge tone={STATE_TONE[row.state]} size="sm">
          {STATE_LABEL[row.state]}
        </StatusBadge>
      ),
    },
    {
      key: "sellerAmount",
      header: "Payout amount",
      align: "right",
      headerHint: "Exact amount transferred or scheduled for transfer to the seller.",
      render: (row) => (
        <Money
          amount={row.sellerAmount}
          currency={row.currency}
          rates={rates}
          layout="inline"
          className="font-medium text-foreground"
        />
      ),
    },
    {
      key: "operatorAmount",
      header: "Operator amount",
      align: "right",
      headerHint: "Amount retained or transferred to the marketplace operator in this cycle.",
      render: (row) => (
        <Money
          amount={row.operatorAmount}
          currency={row.currency}
          rates={rates}
          layout="inline"
          className="text-muted-foreground"
        />
      ),
    },
    {
      key: "voucher",
      header: "Voucher",
      render: (row) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {row.paymentVoucherReference ?? "—"}
        </span>
      ),
    },
  ]
}

/**
 * The account position: three figures in the marketplace's own words, or one
 * sentence when nothing has been captured yet — never three zeros, because zero
 * is a claim that the marketplace owes us nothing while an absent snapshot is the
 * absence of any claim, and confusing the two is what this band exists to end.
 *
 * Captioned with the day the snapshot was captured, not with the selected month.
 * These figures have no month: they answer "where do we stand now", the table
 * answers "what happened in July", and on a page whose selector says a month the
 * as-of date is what stops three unmoving numbers from reading as a bug.
 *
 * The Failed tile rides here as well, and still comes from the CYCLE mirror: the
 * account publishes no notion of a failed transfer at all, so the two reads are
 * complementary rather than redundant. It appears only when something failed.
 */
function BalanceBand({
  balance,
  failedTotals,
  rates,
}: {
  balance: PayoutBalanceResponse | undefined
  failedTotals: PayoutFleetEntry["totals"]
  rates: ExchangeRates | undefined
}) {
  const captured = balance?.balance ?? null
  const hasFailed = failedTotals.length > 0
  const columns = (captured === null ? 1 : BALANCE_FIELDS.length) + (hasFailed ? 1 : 0)

  return (
    <>
      <SectionCaption>
        Account balance
        {captured ? ` · as of ${formatDateParts(captured.capturedOn).date}` : null}
      </SectionCaption>

      <div
        className={cn(
          "grid border-y border-border/70 bg-muted/[0.18] sm:divide-x sm:divide-border/70",
          BAND_COLUMNS[columns]
        )}
      >
        {captured === null ? (
          <div className="px-5 py-4">
            <p className="text-sm text-muted-foreground">
              No balance has been captured yet — the daily payout sync records it.
            </p>
          </div>
        ) : (
          BALANCE_FIELDS.map((field) => (
            <div key={field.key} className="px-5 py-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {field.label}

                <HintMark label={field.label} hint={field.hint} />
              </div>

              <div className="mt-1.5 text-xl font-semibold tracking-tight text-foreground">
                <Money
                  amount={captured[field.key]}
                  currency={captured.currency}
                  rates={rates}
                  layout="stacked"
                />
              </div>
            </div>
          ))
        )}

        {hasFailed ? (
          <SummaryMetric
            label="Failed"
            totals={failedTotals}
            field="failed"
            rates={rates}
            tone="destructive"
          />
        ) : null}
      </div>
    </>
  )
}

/** The small label that names which clock the numbers under it answer to. */
function SectionCaption({ children }: { children: ReactNode }) {
  return (
    <div className="px-5 pb-1.5 pt-4 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  )
}

/** The `?` beside a figure's name. A real button rather than a bare glyph, so a
 *  tap focuses it and the explanation opens on touch as well as on hover. */
function HintMark({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip content={hint}>
      <button
        type="button"
        aria-label={`What ${label} means`}
        className="flex size-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold leading-none text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        ?
      </button>
    </Tooltip>
  )
}

/** One figure per currency — the API already totals each currency exactly, so
 *  nothing here adds amounts up and nothing passes through a float. */
function SummaryMetric({
  label,
  totals,
  field,
  rates,
  tone,
}: {
  label: string
  totals: PayoutFleetEntry["totals"]
  field: "paid" | "pending" | "failed"
  rates: ExchangeRates | undefined
  tone?: "destructive"
}) {
  const values = totals.map((total) => (
    <Money
      key={total.currency}
      amount={total[field]}
      currency={total.currency}
      rates={rates}
      layout="stacked"
    />
  ))
  return (
    <div className="px-5 py-4">
      <div className="text-xs text-muted-foreground">{label}</div>

      <div
        className={cn(
          "mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xl font-semibold tracking-tight",
          tone === "destructive" ? "text-destructive" : "text-foreground"
        )}
      >
        {values.length === 0 ? "—" : values}
      </div>
    </div>
  )
}

function SyncButton({
  onSync,
  isSyncing,
  label = "Sync this month",
  compact = false,
}: {
  onSync: (() => void) | undefined
  isSyncing: boolean
  label?: string
  compact?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSync}
      disabled={isSyncing || !onSync}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card font-medium text-foreground shadow-card transition-colors hover:bg-muted/60 disabled:opacity-60",
        compact ? "h-7 px-3 text-xs" : "mt-3 h-9 px-4 text-sm"
      )}
    >
      <RefreshCw className={cn("size-3.5", isSyncing && "animate-spin")} aria-hidden="true" />

      {isSyncing ? "Syncing…" : label}
    </button>
  )
}

/** `gated` and `unsupported` are listed for exhaustiveness, but the page renders
 *  those marketplaces as collapsed rows rather than as blocks — a marketplace that
 *  can never have a cycle does not need a table to say so. */
const AVAILABILITY_BADGE: Record<PayoutAvailability, { label: string; tone: BadgeTone }> = {
  available: { label: "Complete", tone: "success" },
  partial: { label: "Partial", tone: "warning" },
  not_synced: { label: "Not synced", tone: "muted" },
  gated: { label: "No shop id", tone: "muted" },
  unsupported: { label: "Unavailable", tone: "muted" },
}

function AvailabilityBadge({ availability }: { availability: PayoutAvailability }) {
  const current = AVAILABILITY_BADGE[availability]
  return (
    <StatusBadge tone={current.tone} size="sm">
      {current.label}
    </StatusBadge>
  )
}

function Message({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="px-5 py-8 text-center">
      <div className="text-sm font-medium text-foreground">{title}</div>

      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>

      {action}
    </div>
  )
}

/** True for any decimal-string spelling of zero ("0", "0.00", "-0"). Kept as a
 *  string test so a total never round-trips through a float just to be checked. */
function isZeroDecimal(value: string): boolean {
  return /^-?0*(?:\.0*)?$/.test(value)
}
